import { cn } from "@/lib/cn";
import { type HTMLAttributes, type ReactNode, createContext, useContext, useId } from "react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  type UseFormReturn,
  useFormContext,
} from "react-hook-form";

export const Form = FormProvider;

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ ...props }: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
}

interface FormItemContextValue {
  id: string;
}

const FormItemContext = createContext<FormItemContextValue | null>(null);

export function FormItem({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  const id = useId();
  return (
    <FormItemContext.Provider value={{ id }}>
      <div className={cn("flex flex-col gap-1.5", className)} {...props} />
    </FormItemContext.Provider>
  );
}

function useFormField() {
  const fieldCtx = useContext(FormFieldContext);
  const itemCtx = useContext(FormItemContext);
  const form = useFormContext();
  if (!fieldCtx || !itemCtx) throw new Error("useFormField must be inside <FormField><FormItem>");
  const { name } = fieldCtx;
  const { id } = itemCtx;
  const error = form.getFieldState(name, form.formState).error;
  return {
    id,
    name,
    formItemId: `${id}-form-item`,
    formMessageId: `${id}-form-message`,
    error,
  };
}

export function FormLabel({ className, children }: { className?: string; children: ReactNode }) {
  const { formItemId, error } = useFormField();
  return (
    <label
      htmlFor={formItemId}
      className={cn(
        "text-xs text-[var(--iris-fg-3)]",
        error && "text-[var(--iris-neon-pink)]",
        className,
      )}
    >
      {children}
    </label>
  );
}

export function FormMessage({ className }: { className?: string }) {
  const { error, formMessageId } = useFormField();
  const body = error?.message?.toString();
  if (!body) return null;
  return (
    <span id={formMessageId} className={cn("text-xs text-[var(--iris-neon-pink)]", className)}>
      {body}
    </span>
  );
}

export function useFormFieldBind(): {
  id: string;
  "aria-invalid": boolean | undefined;
  "aria-describedby": string | undefined;
} {
  const { formItemId, formMessageId, error } = useFormField();
  return {
    id: formItemId,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? formMessageId : undefined,
  };
}

export type { UseFormReturn };
